import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw, Send } from 'lucide-react';

import '@/components/onboarding/preview-chat.tokens.css';
import { ChatBubble } from '@/components/onboarding/ChatBubble';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api';
import { HIT_AREA_44 } from '@/lib/touchTarget';
import { cn } from '@/lib/utils';
import {
  previewChatService,
  type PreviewState,
  type SandboxTrace,
} from '@/services/previewChatService';

interface BotSandboxProps {
  businessName: string;
  onMessagesUpdate?: (used: number, cap: number) => void;
}

interface Turn {
  customer: string;
  bot: string;
  trace: SandboxTrace | null;
}

/** Subset of PreviewState/PreviewMessageResponse used for the meter */
type MeterState = Pick<
  PreviewState,
  | 'phase'
  | 'daily_free_used'
  | 'daily_free_cap'
  | 'overflow_source'
  | 'overflow_used'
  | 'overflow_cap'
  | 'messages_used'
  | 'cap'
>;

function buildInitials(name: string): string {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return initials || '?';
}

/**
 * F040 — interactive dry-run sandbox. Renders inside the wizard PreviewSection
 * (step 3) and in the EnrichmentSurface (live mode side column / Testar tab).
 *
 * Session state is purely client-side: `turns` (the transcript) and `slots`
 * (accumulated slot values) are replayed to the server on every send so the
 * backend stays stateless.
 *
 * Token scope: the `.preview-chat-scope` wrapper is the ONLY place where
 * `--preview-chat-*` tokens are consumed. Never reference them outside this
 * component (frontend.md anti-pattern guard).
 *
 * The IG-mimic header (avatar initials + business name) is owned here so both
 * the wizard PreviewSection and the live-mode EnrichmentSurface get it without
 * duplication.
 */
export function BotSandbox({ businessName, onMessagesUpdate }: BotSandboxProps) {
  const { t } = useTranslation();

  const [turns, setTurns] = useState<Turn[]>([]);
  const [slots, setSlots] = useState<Record<string, unknown>>({});
  const [pendingReply, setPendingReply] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [capHit, setCapHit] = useState(false);
  const [meter, setMeter] = useState<MeterState | null>(null);

  const listRef = useRef<HTMLDivElement>(null);

  // F055: fetch initial meter state on mount
  useEffect(() => {
    previewChatService
      .getState()
      .then((state) => {
        setMeter({
          phase: state.phase,
          daily_free_used: state.daily_free_used,
          daily_free_cap: state.daily_free_cap,
          overflow_source: state.overflow_source,
          overflow_used: state.overflow_used,
          overflow_cap: state.overflow_cap,
          messages_used: state.messages_used,
          cap: state.cap,
        });
      })
      .catch(() => {
        // best-effort: meter stays null (hidden) if state fetch fails
      });
  }, []);

  function scrollToBottom() {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }

  async function handleSend() {
    const text = input.trim();
    if (!text || pendingReply || capHit) return;

    setInput('');
    setError(null);

    // Build the history from committed turns (before this new customer message)
    const history = turns.map(({ customer, bot }) => ({ customer, bot }));
    const currentSlots = slots;

    // Optimistically append the customer bubble so the UI feels responsive
    // We use a temporary structure while the bot reply is in flight
    setPendingReply(true);

    // Add customer bubble visually (as a "pending turn" before bot replies)
    setTurns((prev) => [...prev, { customer: text, bot: '', trace: null }]);
    scrollToBottom();

    try {
      const result = await previewChatService.sendMessage(text, history, currentSlots);

      // Replace the pending turn with the completed exchange
      setTurns((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          customer: text,
          bot: result.reply,
          trace: result.trace,
        };
        return next;
      });
      setSlots(result.slots);
      onMessagesUpdate?.(result.messages_used, result.cap);
      // F055: update meter from send response (same fields as getState)
      setMeter({
        phase: result.phase,
        daily_free_used: result.daily_free_used,
        daily_free_cap: result.daily_free_cap,
        overflow_source: result.overflow_source,
        overflow_used: result.overflow_used,
        overflow_cap: result.overflow_cap,
        messages_used: result.messages_used,
        cap: result.cap,
      });
      setTimeout(scrollToBottom, 0);
    } catch (err) {
      // Remove the optimistic customer bubble on failure
      setTurns((prev) => prev.slice(0, -1));
      if (err instanceof ApiError && err.status === 429) {
        setCapHit(true);
      } else {
        setError(t('bot.sandbox.error'));
      }
    } finally {
      setPendingReply(false);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  }

  function handleReset() {
    setTurns([]);
    setSlots({});
    setError(null);
    setCapHit(false);
    setInput('');
    setPendingReply(false);
  }

  const hasContent = turns.length > 0 || pendingReply;

  /** F055: derive the bucket-explicit i18n key + interpolation opts for the meter */
  function getMeterContent(m: MeterState): { key: string; opts: Record<string, unknown> } {
    const {
      phase,
      daily_free_used,
      daily_free_cap,
      overflow_source,
      overflow_used,
      overflow_cap,
      messages_used,
      cap,
    } = m;

    if (phase === 'cadastro') {
      return {
        key: 'bot.sandbox.meter.cadastro',
        opts: { used: messages_used, cap },
      };
    }

    const courtesySpent = overflow_source !== 'none';

    if (phase === 'trial') {
      if (!courtesySpent) {
        return {
          key: 'bot.sandbox.meter.trial_courtesy',
          opts: { used: daily_free_used, cap: daily_free_cap },
        };
      }
      return {
        key: 'bot.sandbox.meter.trial_overflow',
        opts: { used: overflow_used, cap: overflow_cap ?? 30 },
      };
    }

    // active
    if (!courtesySpent) {
      return {
        key: 'bot.sandbox.meter.active_courtesy',
        opts: { used: daily_free_used, cap: daily_free_cap },
      };
    }
    if (overflow_cap === null) {
      return { key: 'bot.sandbox.meter.active_overflow_unlimited', opts: {} };
    }
    return {
      key: 'bot.sandbox.meter.active_overflow_finite',
      opts: { used: overflow_used, cap: overflow_cap },
    };
  }

  const meterContent = meter ? getMeterContent(meter) : null;

  // For the progressbar: determine progress ratio
  function getMeterProgress(m: MeterState): { now: number; max: number } {
    const {
      phase,
      daily_free_used,
      daily_free_cap,
      overflow_source,
      overflow_used,
      overflow_cap,
      messages_used,
      cap,
    } = m;
    if (phase === 'cadastro') return { now: messages_used, max: cap };
    const courtesySpent = overflow_source !== 'none';
    if (!courtesySpent) return { now: daily_free_used, max: daily_free_cap };
    if (overflow_cap === null) return { now: overflow_used, max: overflow_used + 1 };
    return { now: overflow_used, max: overflow_cap };
  }

  return (
    <div data-testid="bot-sandbox" className="preview-chat-scope flex flex-col">
      {/* IG-mimic header — avatar initials + business name */}
      <div
        data-testid="sandbox-header"
        className="flex h-14 items-center gap-2 border-b border-[color:var(--preview-chat-header-border)] px-4"
      >
        <Avatar className="h-7 w-7 shrink-0">
          <AvatarFallback className="text-xs font-medium">
            {buildInitials(businessName)}
          </AvatarFallback>
        </Avatar>
        <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-[color:var(--preview-chat-bot-text)]">
          {businessName}
        </span>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={handleReset}
          data-testid="sandbox-reset"
          aria-label={t('bot.sandbox.reset')}
          className={cn('ml-auto h-9 w-9 shrink-0 rounded-full', HIT_AREA_44)}
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>

      {/* F055: Always-visible meter — monochrome progressbar (no preview-chat tokens) */}
      {meterContent &&
        (() => {
          const { now, max } = getMeterProgress(meter!);
          const pct = max > 0 ? Math.min(100, Math.round((now / max) * 100)) : 0;
          const label = t(meterContent.key, meterContent.opts);
          return (
            <div
              data-testid="sandbox-meter"
              className="px-4 py-2 border-b border-[color:var(--preview-chat-header-border)] space-y-1.5"
            >
              <div
                className="h-1.5 w-full rounded-full bg-muted overflow-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={max}
                aria-valuenow={now}
                aria-label={label}
              >
                <div
                  className="h-full bg-foreground transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          );
        })()}

      {/* Bubble list */}
      <div
        ref={listRef}
        role="log"
        aria-live="polite"
        className="flex min-h-[min(14rem,30dvh)] max-h-[min(24rem,55dvh)] flex-col gap-2 overflow-y-auto p-4 bg-[color:var(--preview-chat-bg)]"
      >
        {!hasContent ? (
          <p
            className="m-auto max-w-[18rem] text-center text-sm text-muted-foreground"
            data-testid="sandbox-empty"
          >
            {t('bot.sandbox.empty')}
          </p>
        ) : (
          <>
            {turns.map((turn, i) => (
              <div key={i} className="flex flex-col gap-1">
                <ChatBubble role="customer" text={turn.customer} />
                {turn.bot && <ChatBubble role="bot" text={turn.bot} />}
                {turn.bot && turn.trace && <TraceBlock trace={turn.trace} />}
              </div>
            ))}
            {pendingReply && <ChatBubble role="bot" pending />}
          </>
        )}
      </div>

      {/* Error */}
      {error && (
        <div
          role="alert"
          data-testid="sandbox-error"
          className="mx-4 mb-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      {/* Cap banner */}
      {capHit && (
        <div
          data-testid="sandbox-cap-banner"
          className="border-t border-[color:var(--preview-chat-header-border)] p-4 text-center text-sm text-muted-foreground"
          role="status"
        >
          {t('bot.sandbox.cap_hit')}
        </div>
      )}

      {/* Composer — always visible unless cap hit */}
      {!capHit && (
        <div className="flex items-end gap-2 border-t border-[color:var(--preview-chat-header-border)] bg-[color:var(--preview-chat-bg)] p-3">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder={t('bot.sandbox.composer_placeholder')}
            aria-label={t('bot.sandbox.composer_placeholder')}
            disabled={pendingReply}
            className="min-h-[2.75rem] max-h-[6rem] resize-none rounded-2xl border-[color:var(--preview-chat-composer-border)] bg-[color:var(--preview-chat-composer-bg)]"
          />
          <Button
            type="button"
            size="icon"
            onClick={() => void handleSend()}
            disabled={!input.trim() || pendingReply}
            aria-label={t('bot.sandbox.send')}
            className="h-11 w-11 shrink-0 rounded-full"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Trace block ───────────────────────────────────────────────────────────────

interface TraceBlockProps {
  trace: SandboxTrace;
}

function TraceBlock({ trace }: TraceBlockProps) {
  const { t } = useTranslation();

  const chips: string[] = [];

  if (trace.would_register_lead) {
    chips.push(t('bot.sandbox.trace.register'));
  }

  if (trace.register_gated) {
    const key =
      (trace.register_gated.missing?.length ?? 0) > 0
        ? 'bot.sandbox.trace.register_gated'
        : 'bot.sandbox.trace.register_gated_unconfirmed';
    chips.push(t(key));
  }

  for (const link of trace.would_send_links) {
    chips.push(t('bot.sandbox.trace.send_link', { label: link.label }));
  }

  if (trace.would_escalate) {
    chips.push(t('bot.sandbox.trace.escalate'));
  }

  if (trace.slots_progress.length > 0) {
    const items = trace.slots_progress
      .map((s) => (s.status === 'collected' ? `${s.label} ✓` : `${s.label} ⏳`))
      .join(' · ');
    chips.push(t('bot.sandbox.trace.progress', { items }));
  }

  if (chips.length === 0) return null;

  return (
    <div data-testid="sandbox-trace" className="ml-2 flex flex-wrap gap-1.5">
      {chips.map((chip, i) => (
        <span
          key={i}
          className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-inset ring-border"
        >
          {chip}
        </span>
      ))}
    </div>
  );
}
